import AsyncStorage from "@react-native-async-storage/async-storage";
import { useCallback, useEffect, useState } from "react";
import { Platform } from "react-native";

/**
 * Whether this device can install the app, and how.
 *
 * Three separate realities, which is why this is a hook rather than a boolean:
 *
 *  - **Chromium (Android, desktop)** fires `beforeinstallprompt`. We hold the
 *    event and fire `prompt()` later, on a real tap — the browsers require a user
 *    gesture, and the event is single-use.
 *  - **iOS Safari** has no prompt at all: the user has to go through Share → Add
 *    to Home Screen, so the app can only explain it. Only Safari can do it; Chrome
 *    or an in-app browser on iOS cannot install at all, so it must not be told to
 *    try.
 *  - **Already installed** — running in `display-mode: standalone` — means there is
 *    nothing to offer, and the UI should disappear rather than say "installed".
 *
 * Every browser API is touched inside an effect: the web export is pre-rendered on
 * Node, where `window` does not exist, and reading it during render would both
 * crash the export and desync hydration.
 */

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

declare global {
  interface Window {
    /**
     * Stashed by the bootstrap in `app/+html.tsx`, which runs before the bundle.
     * Chromium fires `beforeinstallprompt` during page load, so a hook that only
     * listened on mount would never see it.
     */
    __clubInstallPrompt?: BeforeInstallPromptEvent | null;
  }
}

/** Announced by the bootstrap whenever the stashed prompt changes. */
const INSTALL_PROMPT_READY_EVENT = "club:install-prompt-ready";

export type InstallOutcome = "accepted" | "dismissed" | "unavailable";

/** Dismissal is per device and permanent: the banner is a one-time offer. */
export const INSTALL_BANNER_DISMISSED_KEY = "club:pwa-install-dismissed";

const isWeb = Platform.OS === "web";

/** iPadOS 13+ reports itself as a Mac, so touch points are the tell. */
const detectIos = (): boolean => {
  if (!isWeb || typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
};

/**
 * Safari specifically. On iOS every browser is WebKit, but only Safari can add to
 * the home screen — Chrome/Firefox/Instagram's in-app browser cannot, so the
 * instructions must not be shown there.
 */
const detectIosSafari = (): boolean => {
  if (!detectIos()) return false;
  const ua = navigator.userAgent;
  return (
    /Safari/.test(ua) &&
    !/(CriOS|FxiOS|EdgiOS|OPiOS|GSA|Instagram|FBAN|FBAV)/.test(ua)
  );
};

const detectStandalone = (): boolean => {
  if (!isWeb || typeof window === "undefined") return false;
  const iosStandalone = (window.navigator as { standalone?: boolean })
    .standalone;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
    iosStandalone === true
  );
};

export type PwaInstall = {
  /** Chromium handed us a prompt we can fire on a tap. */
  canPrompt: boolean;
  /** iOS Safari: instructions instead of a prompt. */
  showIosSteps: boolean;
  /** iOS, but a browser that cannot install — say so rather than mislead. */
  iosNeedsSafari: boolean;
  /** Running from the home screen already. */
  isInstalled: boolean;
  /** The feed banner was dismissed on this device. */
  isBannerDismissed: boolean;
  dismissBanner: () => void;
  /** Fire the Chromium prompt; `unavailable` when no event was captured. */
  promptInstall: () => Promise<InstallOutcome>;
};

export const usePwaInstall = (): PwaInstall => {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(
    null,
  );
  const [isInstalled, setIsInstalled] = useState(false);
  const [showIosSteps, setShowIosSteps] = useState(false);
  const [iosNeedsSafari, setIosNeedsSafari] = useState(false);
  const [isBannerDismissed, setBannerDismissed] = useState(true);

  useEffect(() => {
    if (!isWeb) return;

    setIsInstalled(detectStandalone());
    setShowIosSteps(detectIosSafari() && !detectStandalone());
    setIosNeedsSafari(detectIos() && !detectIosSafari() && !detectStandalone());

    const onBeforeInstallPrompt = (event: Event) => {
      // Chrome still shows its own mini-infobar otherwise, and the event may only
      // be used once — so it is held until the user actually asks.
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      window.__clubInstallPrompt = null;
      setDeferred(null);
      setIsInstalled(true);
    };

    // Already caught before this hook existed? Take it, and follow the
    // announcement for anything that arrives later in the page's life.
    const syncFromBootstrap = () =>
      setDeferred(window.__clubInstallPrompt ?? null);
    syncFromBootstrap();

    window.addEventListener(INSTALL_PROMPT_READY_EVENT, syncFromBootstrap);
    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);

    // Nothing to offer once it is installed, even mid-session.
    const media = window.matchMedia?.("(display-mode: standalone)");
    const onModeChange = () => setIsInstalled(detectStandalone());
    media?.addEventListener?.("change", onModeChange);

    AsyncStorage.getItem(INSTALL_BANNER_DISMISSED_KEY)
      .then((value) => setBannerDismissed(value === "1"))
      .catch(() => setBannerDismissed(false));

    return () => {
      window.removeEventListener(INSTALL_PROMPT_READY_EVENT, syncFromBootstrap);
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      media?.removeEventListener?.("change", onModeChange);
    };
  }, []);

  const dismissBanner = useCallback(() => {
    setBannerDismissed(true);
    AsyncStorage.setItem(INSTALL_BANNER_DISMISSED_KEY, "1").catch(() => {});
  }, []);

  const promptInstall = useCallback(async (): Promise<InstallOutcome> => {
    if (!deferred) return "unavailable";
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    // Single use, whatever the user answered — and every other mounted instance
    // (the banner and the settings card use this hook independently) must drop its
    // own copy, or it would fire a spent event.
    window.__clubInstallPrompt = null;
    window.dispatchEvent(new Event(INSTALL_PROMPT_READY_EVENT));
    setDeferred(null);
    if (outcome === "accepted") setIsInstalled(true);
    return outcome;
  }, [deferred]);

  return {
    canPrompt: deferred !== null,
    showIosSteps,
    iosNeedsSafari,
    isInstalled,
    isBannerDismissed,
    dismissBanner,
    promptInstall,
  };
};
